import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Loader2, Sparkles, Save, Download, FileDown, BadgeCheck, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { api, formatErr, startReviewCheckout, usePoll, downloadFile } from "@/lib/api";
import { CheckoutDisclosure } from "@/components/FundingDisclosure";
import { useAuth } from "@/context/AuthContext";
import { REVIEW_PRICE, REVIEW_MEMBER_PRICE } from "@/lib/constants";
import { CommentsPanel } from "@/components/CommentsPanel";
import { SourcesPanel, SourceCode, prepPreview } from "@/components/SourcesPanel";
import { MissingInfoPanel } from "@/components/proposal/MissingInfoPanel";
import { FunderChecklist } from "@/components/proposal/FunderChecklist";
import { ProofAttachments } from "@/components/proposal/ProofAttachments";
import { toast } from "sonner";

export default function ProposalEditor() {
  const { id } = useParams();
  const [params, setParams] = useSearchParams();
  const { hasPkg } = useAuth();
  const [p, setP] = useState(null);
  const [content, setContent] = useState("");
  const [gen, setGen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [buying, setBuying] = useState(false);
  const [tab, setTab] = useState("preview");
  const [labels, setLabels] = useState({});
  const [attKey, setAttKey] = useState(0);
  const [ckKey, setCkKey] = useState(0);
  const started = useRef(false);

  const generate = useCallback(async () => {
    setTab("preview");
    try {
      await api.post(`/proposals/${id}/generate`);
      setContent("");
      setGen(true);
    } catch (e) {
      toast.error(formatErr(e));
    }
  }, [id]);

  const poll = useCallback(async () => {
    const { data } = await api.get(`/proposals/${id}`);
    setContent(data.content || "");
    if (!["generating", "queued"].includes(data.generation_status)) {
      setGen(false);
      if (data.generation_status === "error") toast.error("Generation stopped early. Try Regenerate.");
      else toast.success("Proposal generated and saved");
    }
  }, [id]);
  usePoll(poll, gen, 1500);

  useEffect(() => {
    api.get(`/proposals/${id}`).then((r) => {
      setP(r.data);
      setContent(r.data.content || "");
      if (["generating", "queued"].includes(r.data.generation_status)) setGen(true);
      else if (params.get("autogen") && !r.data.content && !started.current) {
        started.current = true;
        setParams({}, { replace: true });
        generate();
      }
    });
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/proposals/${id}`, { content });
      toast.success("Saved");
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setSaving(false);
    }
  };
  const downloadWord = async () => {
    try {
      await api.put(`/proposals/${id}`, { content });
      await downloadFile(`/proposals/${id}/docx`, `${(p?.title || "proposal").replace(/\W+/g, "_")}.docx`);
    } catch (e) { toast.error(formatErr(e)); }
  };
  const download = async () => {
    try {
      await api.put(`/proposals/${id}`, { content });
      await downloadFile(`/proposals/${id}/pdf`, `${(p?.title || "proposal").replace(/\W+/g, "_")}.pdf`);
    } catch (e) {
      toast.error(formatErr(e));
    }
  };
  const review = async () => {
    setBuying(true);
    try {
      await api.put(`/proposals/${id}`, { content });
      await startReviewCheckout(id);
    } catch (e) {
      toast.error(formatErr(e));
      setBuying(false);
    }
  };

  if (!p) return <Loader2 className="h-6 w-6 animate-spin text-emerald-600" />;
  const price = hasPkg("suite") ? REVIEW_MEMBER_PRICE : REVIEW_PRICE;
  return (
    <div>
      <Link to="/dashboard/proposals" className="text-sm text-slate-500 hover:text-slate-900 flex items-center gap-1 mb-6" data-testid="back-to-proposals-link"><ArrowLeft className="h-4 w-4" />Proposals</Link>
      <div className="flex flex-col lg:flex-row justify-between gap-4 mb-8">
        <div>
          <div className="eyebrow mb-2">Area 01 · Proposal</div>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900" data-testid="proposal-title">{p.title}</h1>
          <p className="text-slate-500 mt-1">{p.org_name}{p.grant ? ` → ${p.grant.title}` : ""}</p>
        </div>
        <div className="flex flex-wrap gap-2 items-start">
          <Button onClick={generate} disabled={gen} variant="outline" data-testid="generate-proposal-button">{gen ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Sparkles className="h-4 w-4 mr-2" />}{content ? "Regenerate" : "Generate with AI"}</Button>
          <Button onClick={save} disabled={gen || saving} variant="outline" data-testid="save-proposal-button">{saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}Save</Button>
          <Button onClick={download} disabled={!content || gen} variant="outline" data-testid="download-proposal-button"><Download className="h-4 w-4 mr-2" />Download PDF</Button>
          <Button onClick={downloadWord} disabled={!content || gen} variant="outline" data-testid="download-proposal-word-button"><FileDown className="h-4 w-4 mr-2" />Download Word</Button>
        </div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <div className="lg:col-span-8">
          <Tabs value={tab} onValueChange={setTab}>
            <TabsList><TabsTrigger value="preview" data-testid="proposal-preview-tab">Preview</TabsTrigger><TabsTrigger value="edit" disabled={gen} data-testid="proposal-edit-tab">Edit</TabsTrigger></TabsList>
            <TabsContent value="preview">
              <div id="proposal-doc" className="bg-white border border-slate-200 rounded-lg p-8 md:p-12 min-h-[400px] prose-gv" data-testid="proposal-preview">
                {content ? <ReactMarkdown remarkPlugins={[remarkGfm]} components={{ code: ({ children }) => <SourceCode labels={labels}>{children}</SourceCode> }}>{prepPreview(content)}</ReactMarkdown> : <p className="text-slate-400">{gen ? "Writing your proposal…" : "No content yet. Click Generate with AI."}</p>}
                {gen && <span className="inline-block w-2 h-4 bg-emerald-500 animate-pulse align-middle" />}
              </div>
            </TabsContent>
            <TabsContent value="edit">
              <Textarea value={content} onChange={(e) => setContent(e.target.value)} className="min-h-[600px] font-mono text-sm bg-white" data-testid="proposal-content-editor" />
            </TabsContent>
          </Tabs>
        </div>
        <aside className="lg:col-span-4 space-y-4">
          <SourcesPanel proposalId={id} content={content} onContent={setContent} onLabels={setLabels} disabled={gen} />
          <MissingInfoPanel proposalId={id} content={content} onContent={setContent} disabled={gen} />
          {!gen && <FunderChecklist proposalId={id} content={content} reloadKey={attKey} onReady={() => setCkKey((k) => k + 1)} />}
          <ProofAttachments proposalId={id} reloadKey={ckKey} onChange={() => setAttKey((k) => k + 1)} />
          <div className="bg-white border border-slate-200 border-l-4 border-l-amber-500 rounded-lg p-6" data-testid="human-review-upsell-card">
            <BadgeCheck className="h-7 w-7 text-amber-600" />
            <h3 className="font-display text-xl font-bold text-slate-900 mt-4">Certified human final review</h3>
            <p className="text-sm text-slate-600 mt-2 leading-relaxed">A certified Peer Verify grant editor checks your proposal line by line for compliance, suggests edits and certifies it.</p>
            <div className="mt-5 flex items-baseline gap-2"><span className="font-display text-4xl font-extrabold text-slate-900" data-testid="human-review-price">${price}</span><span className="text-sm text-slate-500">flat fee</span></div>
            <Button onClick={review} disabled={!content || gen || buying} className="w-full mt-5 bg-amber-600 hover:bg-amber-700" data-testid="submit-human-review-button">
              {buying && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Send for human review
            </Button>
            <CheckoutDisclosure className="mt-3" id="human-review" />
            {!content && <p className="text-xs text-slate-400 mt-2">Generate the proposal first.</p>}
          </div>
          <CommentsPanel proposalId={id} containerId="proposal-doc" />
        </aside>
      </div>
    </div>
  );
}
